import InfoIcon from "@mui/icons-material/Info";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { PlateBarcodeField } from "features/WorklistTools/shared/components/PlateBarcodeField";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import {
  PlatePosition,
  WorklistTools,
} from "features/WorklistTools/shared/interfaces";
import { getPlateWellIdFromRowAndColIndex } from "features/WorklistTools/shared/WorklistHelpers";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import styled from "styled-components";
import { getIntTotalVol } from "../NucleofectionToolHelpers";
import {
  IntermediatePlateInfoState,
  NucleofectionPlateInfoState,
  NucleofectionToolActions,
  WorklistWellMappingState,
} from "../state";
import { useNucleofectionStateProvider } from "../state/StateProvider";
import { IntermediatePlate } from "./IntermediatePlate";

const StyledIntPlatesContainer = styled.div`
  height: calc(100vh - 300px);
  width: 600px;
  overflow: auto;
`;

const StyledPlateSettingsContainer = styled.div`
  margin-top: 10px;
`;

const StyledIntermediatePlateDiv = styled("div")`
  height: 320px;
  width: 100%;
`;

const StyledPlateActions = styled.div`
  width: 100%;
  display: flex;
  margin-top: 7px;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 10px;
`;

export const IntermediatePlatesContainer = (intPlateTypeInfo: any) => {
  const { nucleofectionInternalState } = useNucleofectionStateProvider();
  const dispatch = useAppReduxDispatch();

  const nucPlateInfo: NucleofectionPlateInfoState[] = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.nucPlateInfo
  );
  const intPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.intPlateInfo
  );
  const worklistValues = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.worklistValues
  );

  const getNucWells = (index: number) => {
    const nucToIntMapping = [];
    let row = 1;
    let col = 1;
    for (const well of worklistValues.sourceToNuc) {
      if (row === 7) {
        row = 1;
        col++;
      }
      if (col === 11) break;
      const wellMapping: WorklistWellMappingState = {
        sourcePlateIndex: well.destPlateIndex,
        sourceWellId: well.destWellId,
        destPlateIndex: index,
        destWellId: getPlateWellIdFromRowAndColIndex(row, col),
      };
      nucToIntMapping.push(wellMapping);
      row++;
    }
    return nucToIntMapping;
  };

  const handlePlateTypeChanged = (index: number, plateTypeInfo: any) => {
    const wellMapping = getNucWells(index);
    dispatch(
      NucleofectionToolActions.SET_INTERMEDIATE_PLATE({
        index,
        plateBarcode: intPlateInfo[index].plateBarcode,
        rows: plateTypeInfo.plateRows,
        cols: plateTypeInfo.plateCols,
        operatingVol: plateTypeInfo.defaultLabwareVolume,
        labwareTypeCode: plateTypeInfo.labwareTypeCode,
      })
    );
    dispatch(
      NucleofectionToolActions.NUC_TO_INT_ADDED({
        selections: wellMapping,
      })
    );
  };

  const handleIntPlateInfoChanged = (
    index: number,
    key: string,
    value: any
  ) => {
    dispatch(
      NucleofectionToolActions.UPDATE_INT_PLATE_INFO({
        index,
        key,
        value,
      })
    );
  };

  const handleRemovePlateClick = (index: number) => {
    dispatch(
      NucleofectionToolActions.CLEAR_INTERMEDIATE_PLATE({
        index,
      })
    );
  };

  const intTotalVol = getIntTotalVol(
    nucPlateInfo[0].topUpVol,
    nucPlateInfo[0].washVol,
    nucPlateInfo[0].numberOfWashes
  );

  return (
    <StyledIntPlatesContainer>
      {intPlateInfo.map((item: IntermediatePlateInfoState, index: number) => (
        <div style={{ padding: "20px" }}>
          <PlateBarcodeField
            plateRole={PlatePosition.Intermediate}
            index={index}
            label={`Intermediate Plate Barcode`}
            plateBarcode={item.plateBarcode}
            handlePlateBarcodeChange={(index, field, value) =>
              handleIntPlateInfoChanged(index!, field, value)
            }
          />
          <StyledIntermediatePlateDiv>
            <IntermediatePlate
              worklistTool={WorklistTools.Nucleofection}
              index={index}
              plateRows={item.rows}
              plateCols={item.cols}
              wellMapping={worklistValues.nucToInt}
              nucPlateInfo={nucPlateInfo}
            />
          </StyledIntermediatePlateDiv>
          <StyledPlateActions>
            <Button
              variant="outlined"
              disabled={intPlateInfo[index].rows.length < 1}
              onClick={() => handleRemovePlateClick(index)}
            >
              Clear Plate
            </Button>
          </StyledPlateActions>
          <StyledPlateSettingsContainer>
            <PlateType
              platePosition={PlatePosition.Intermediate}
              isDisabled={
                nucleofectionInternalState.methodSettings!.selectedSystem ===
                  0 || nucPlateInfo[index].rows.length === 0
              }
              acceptedPlateTypes={intPlateTypeInfo.intPlateInfo}
              plateType={item.labwareTypeCode}
              setPlateType={(plateTypeInfo) =>
                handlePlateTypeChanged(index, plateTypeInfo)
              }
            />
          </StyledPlateSettingsContainer>
          {item.rows.length > 0 ? (
            <div style={{ display: "flex", width: "100%" }}>
              <NumberField
                style={{
                  width: "100%",
                  marginTop: "10px",
                  marginRight: "5px",
                }}
                id="operating-vol"
                label="Operating Vol (µL)"
                value={item.operatingVol}
                onChange={(value) =>
                  handleIntPlateInfoChanged(
                    index,
                    "operatingVol",
                    parseInt(value)
                  )
                }
                disabled={true}
              />
              <NumberField
                style={{
                  width: "100%",
                  marginTop: "10px",
                  marginRight: "5px",
                }}
                id="total-vol"
                label={
                  <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                    Total Vol (µL)
                    <Tooltip
                      arrow
                      placement="top"
                      title="(Nucleofection Top Up Vol) + (Wash Cycles x Wash Vol)"
                    >
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
                value={intTotalVol}
                onChange={() => {
                  return;
                }} //Computed Value
                disabled
              />
            </div>
          ) : null}
        </div>
      ))}
    </StyledIntPlatesContainer>
  );
};
